import { type QuestionnaireDefinitionDto, usePerformanceMeasureDefinition } from "@repo/data-store";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CatalogDefinition, QuestionnaireRead } from "@/features/catalog";
import { useDefinitionChangePoints, useDefinitionView } from "@/features/catalog/hooks/use-definition-as-of";
import { asOfSearchSchema } from "@/features/catalog/lib/as-of-search";
import { questionnaireDefinitionUrlFromRouteId } from "@/features/catalog/lib/questionnaire-definition-route";

export const Route = createFileRoute("/catalog/performance-measures/$questionnaireId")({
  component: QuestionnaireDetailComponent,
  validateSearch: asOfSearchSchema,
});

function QuestionnaireDetailComponent() {
  const { questionnaireId } = Route.useParams();
  const { asOf } = Route.useSearch();
  const { permissions } = Route.useRouteContext();
  const navigate = useNavigate();
  const definitionUrl = questionnaireDefinitionUrlFromRouteId(questionnaireId);
  const live = usePerformanceMeasureDefinition(asOf ? undefined : definitionUrl);
  const view = useDefinitionView<QuestionnaireDefinitionDto>(definitionUrl, asOf, live);
  const { changePoints } = useDefinitionChangePoints(definitionUrl);

  if (view.isPending) return <CatalogDefinition.Pending />;
  if (!view.definition) {
    return (
      <CatalogDefinition.NotFound
        label="Performance measure"
        backTo="/catalog/performance-measures"
        backLabel="Back to performance measures"
      />
    );
  }

  return (
    <QuestionnaireRead
      performance
      key={`${questionnaireId}|${asOf ?? "latest"}`}
      questionnaire={view.definition}
      asOf={asOf}
      changePoints={changePoints}
      canEdit={!view.isPast && permissions.canWrite("Questionnaire")}
      onRevised={() => navigate({ to: "/catalog/performance-measures" })}
    />
  );
}
